import React, { useEffect, useState, useCallback } from "react";
import {
  Page,
  LegacyCard,
  Text,
  Badge,
  Button,
  Modal,
  Select,
  TextField,
  FormLayout,
  Spinner,
  InlineStack,
  BlockStack,
} from "@shopify/polaris";
import { useNavigate, useParams } from "react-router-dom";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { UserAuth } from "../../context/AuthContext";
import { formatPrice, formatFriendlyDate, formatPlate } from "../../utility/funcs";
import moment from "moment";

const PAYMENT_METHODS = [
  { name: "Efectivo", accounts: [1] },
  { name: "Tarjeta de Crédito", accounts: [4, 5] },
  { name: "Tarjeta de Débito", accounts: [4, 5] },
  { name: "Transferencia", accounts: [2, 3] },
  { name: "Depósito en Efectivo", accounts: [2, 3] },
];

function Infraction() {
  const { infractionId } = useParams();
  const navigate = useNavigate();
  const { session } = UserAuth();

  const [infraction, setInfraction] = useState(null);
  const [loading, setLoading] = useState(true);

  const [payOpen, setPayOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [method, setMethod] = useState("Efectivo");
  const [cardLast4, setCardLast4] = useState("");
  const [account, setAccount] = useState(1);
  const [paymentDate, setPaymentDate] = useState(moment().format("YYYY-MM-DD"));
  const [paying, setPaying] = useState(false);

  const fetchInfraction = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("infractions")
      .select(
        "id, infraction_date, plate, amount, reason, paid, paid_at, created_at, customer_id, company_id, reservation_id, car_id, paid_transaction_id, customer:customer_id(id, first_name, last_name, phone, email), company:company_id(id, name), car:car_id(id, plate, model:model(name, brand:brand(name))), reservation:reservation_id(id), tx:paid_transaction_id(id, amount, payment_date, account_info:account(name))"
      )
      .eq("id", infractionId)
      .single();
    if (error) console.error(error);
    setInfraction(data);
    setLoading(false);
  }, [infractionId]);

  useEffect(() => {
    fetchInfraction();
  }, [fetchInfraction]);

  useEffect(() => {
    if (!payOpen) return;
    (async () => {
      const { data } = await supabase
        .from("transaction_accounts")
        .select("id, name")
        // Clip and Fintoc feed themselves; see transaction_accounts.manual_entry.
        .eq("manual_entry", true)
        .order("order", { ascending: true });
      setAccounts(data || []);
    })();
  }, [payOpen]);

  const availableAccounts = (accounts || []).filter((a) =>
    PAYMENT_METHODS.find((m) => m.name === method)?.accounts.includes(a.id)
  );

  useEffect(() => {
    const allowed = PAYMENT_METHODS.find((m) => m.name === method)?.accounts || [];
    if (!allowed.includes(account)) setAccount(allowed[0]);
  }, [method, account]);

  const confirmPay = async () => {
    if (!infraction) return;
    let cardType = null;
    if (method === "Tarjeta de Crédito") cardType = "credit";
    else if (method === "Tarjeta de Débito") cardType = "debit";
    if (cardType && !/^\d{4}$/.test(cardLast4)) {
      alert("Ingresa los últimos 4 dígitos de la tarjeta.");
      return;
    }
    setPaying(true);

    const billingEntityType = infraction.company_id ? "company" : "customer";
    const billingEntityId = infraction.company_id || infraction.customer_id;
    const { data: tx, error: txErr } = await supabase
      .from("transactions")
      .insert({
        type: "income",
        amount: infraction.amount,
        reservation: infraction.reservation_id || null,
        customer: infraction.customer_id || null,
        isDeposit: false,
        account: account,
        card_type: cardType,
        card_last4: cardType ? (cardLast4 || null) : null,
        payment_date: paymentDate,
        unallocated_amount: 0,
        billing_entity_type: billingEntityType,
        billing_entity_id: billingEntityId,
        invoiced: true,
        infraction_id: infraction.id,
        created_by: session?.user?.id || null,
      })
      .select("id")
      .single();

    if (txErr) {
      setPaying(false);
      alert("Error: " + txErr.message);
      return;
    }

    const { error: updErr } = await supabase
      .from("infractions")
      .update({
        paid: true,
        paid_at: new Date().toISOString(),
        paid_transaction_id: tx.id,
      })
      .eq("id", infraction.id);

    setPaying(false);
    if (updErr) {
      alert("Error: " + updErr.message);
      return;
    }

    setPayOpen(false);
    fetchInfraction();
  };

  if (loading) {
    return (
      <AppWrapper>
        <Page>
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spinner size="small" />
          </div>
        </Page>
      </AppWrapper>
    );
  }

  if (!infraction) {
    return (
      <AppWrapper>
        <Page>
          <Text>Infracción no encontrada.</Text>
        </Page>
      </AppWrapper>
    );
  }

  const isCompany = !!infraction.company_id;
  const clientLabel = infraction.customer
    ? `${infraction.customer.first_name || ""} ${infraction.customer.last_name || ""}`.trim()
    : infraction.company
      ? infraction.company.name
      : "—";

  return (
    <AppWrapper>
      <Page
        title={`Infracción #${infraction.id}`}
        backAction={{ onAction: () => navigate("/infractions") }}
        titleMetadata={
          <Badge tone={infraction.paid ? "success" : "warning"}>
            {infraction.paid ? "Pagada" : "Pendiente"}
          </Badge>
        }
        primaryAction={
          !infraction.paid
            ? {
                content: "Marcar como Pagada",
                onAction: () => setPayOpen(true),
              }
            : undefined
        }
      >
        <LegacyCard sectioned title="Detalles">
          <BlockStack gap="200">
            <InlineStack gap="400">
              <Text variant="bodySm" tone="subdued">Fecha:</Text>
              <Text variant="bodySm">{formatFriendlyDate(infraction.infraction_date)}</Text>
            </InlineStack>
            <InlineStack gap="400">
              <Text variant="bodySm" tone="subdued">Auto:</Text>
              <Text variant="bodySm">
                {infraction.car?.model?.brand?.name} {infraction.car?.model?.name} ·{" "}
                {formatPlate(infraction.plate)}
              </Text>
            </InlineStack>
            <InlineStack gap="400">
              <Text variant="bodySm" tone="subdued">
                {isCompany ? "Empresa:" : "Cliente:"}
              </Text>
              <Button
                variant="plain"
                onClick={() =>
                  navigate(
                    isCompany
                      ? `/companies/edit?id=${infraction.company_id}`
                      : `/customers/edit?id=${infraction.customer_id}`
                  )
                }
              >
                {clientLabel}
              </Button>
            </InlineStack>
            <InlineStack gap="400">
              <Text variant="bodySm" tone="subdued">Reserva:</Text>
              {infraction.reservation_id ? (
                <Button
                  variant="plain"
                  onClick={() =>
                    navigate(`/reservation?id=${infraction.reservation_id}`)
                  }
                >
                  #{infraction.reservation_id}
                </Button>
              ) : (
                <Text variant="bodySm" tone="subdued">Sin reserva</Text>
              )}
            </InlineStack>
            <InlineStack gap="400">
              <Text variant="bodySm" tone="subdued">Monto:</Text>
              <Text variant="bodySm" fontWeight="semibold">{formatPrice(infraction.amount)}</Text>
            </InlineStack>
            <div>
              <Text variant="bodySm" tone="subdued">Motivo:</Text>
              <Text variant="bodySm">{infraction.reason}</Text>
            </div>
          </BlockStack>
        </LegacyCard>

        {infraction.paid && infraction.tx && (
          <LegacyCard sectioned title="Pago">
            <BlockStack gap="200">
              <InlineStack gap="400">
                <Text variant="bodySm" tone="subdued">Fecha de pago:</Text>
                <Text variant="bodySm">{formatFriendlyDate(infraction.tx.payment_date)}</Text>
              </InlineStack>
              <InlineStack gap="400">
                <Text variant="bodySm" tone="subdued">Cuenta:</Text>
                <Text variant="bodySm">{infraction.tx.account_info?.name || "—"}</Text>
              </InlineStack>
              <InlineStack gap="400">
                <Text variant="bodySm" tone="subdued">Monto:</Text>
                <Text variant="bodySm">{formatPrice(infraction.tx.amount)}</Text>
              </InlineStack>
            </BlockStack>
          </LegacyCard>
        )}

        <Modal
          open={payOpen}
          onClose={() => setPayOpen(false)}
          title={`Pagar ${formatPrice(infraction.amount)}`}
          primaryAction={{
            content: "Confirmar Pago",
            onAction: confirmPay,
            loading: paying,
            disabled:
              (method === "Tarjeta de Crédito" || method === "Tarjeta de Débito") &&
              !/^\d{4}$/.test(cardLast4),
          }}
          secondaryActions={[{ content: "Cancelar", onAction: () => setPayOpen(false) }]}
        >
          <Modal.Section>
            <FormLayout>
              <Select
                label="Método de Pago"
                options={PAYMENT_METHODS.map((m) => ({ label: m.name, value: m.name }))}
                value={method}
                onChange={setMethod}
              />
              <Select
                label="Cuenta"
                options={availableAccounts.map((a) => ({ label: a.name, value: String(a.id) }))}
                value={String(account || "")}
                onChange={(v) => setAccount(parseInt(v))}
              />
              {(method === "Tarjeta de Crédito" || method === "Tarjeta de Débito") && (
                <TextField
                  label="Últimos 4 dígitos de la tarjeta"
                  type="text"
                  inputMode="numeric"
                  maxLength={4}
                  value={cardLast4}
                  onChange={(v) => setCardLast4(v.replace(/\D/g, "").slice(0, 4))}
                  error={cardLast4 && !/^\d{4}$/.test(cardLast4) ? "4 dígitos" : undefined}
                  helpText="Requerido para conciliación de tarjeta"
                  autoComplete="off"
                />
              )}
              <TextField
                label="Fecha de Pago"
                type="date"
                value={paymentDate}
                onChange={setPaymentDate}
                autoComplete="off"
              />
            </FormLayout>
          </Modal.Section>
        </Modal>
      </Page>
    </AppWrapper>
  );
}

export default Infraction;
